import { authFetch } from '../../../api/csrf';
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  CheckCircle,
  XCircle,
  Target,
  BarChart3,
  Download,
  Eye,
  Edit,
  FileText,
  Clock,
  Activity,
  Calendar,
  Building,
  ArrowLeft,
  Search,
  Filter,
  Trash2,
  Phone,
  Mail,
  X,
  Plus,
  Package,
  Briefcase,
  User,
  IndianRupee,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  ChevronDown
} from 'lucide-react';

import Button from '../../../components/common/Button/Button';
import {
  handleTextInputChange,
  handleNumberInputChange,
  getCharacterCount,
  validateRequiredFields,
  FIELD_LIMITS,
} from '../../../utils/inputValidation';
import PermissionGate from '../../../components/common/PermissionGate';
import quotationDashboardService from '../../../api/crm/quotation-dashboard/quotationDashboardService';
import opportunityService from '../../../api/crm/opportunities/opportunityService';
import { formatDate } from '../../../utils/stringUtils';
import { formatIndianAmount } from '../../../utils/amountFormatter';
import { toast } from 'react-hot-toast';
import { RefreshCw } from 'lucide-react';
import ExportModal from '../../../components/common/ExportModal';
import SelectedExportFormatBar from '../../../components/common/SelectedExportFormatBar';
import { useQueryClient } from '@tanstack/react-query';
import DateRangeFilter from '../../../components/common/DateRangeFilter';
import { useAuth } from '../../../context/AuthContext';
import { API_ENDPOINTS } from '../../../api/config';
import { USER_ROLES } from '../../../utils/constants';

const CloseWonLost = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [showOrderViewModal, setShowOrderViewModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isCreatingOrder, setIsCreatingOrder] = useState(false);
  const [orders, setOrders] = useState([]);
  const [orderForm, setOrderForm] = useState({
    opportunityId: '',
    orderNumber: '',
    customerName: '',
    customerEmail: '',
    billingAddress: '',
    shippingAddress: '',
    products: [],
    subtotal: 0,
    tax: 0,
    total: 0,
    paymentTerms: 'Net 30',
    deliveryDate: '',
    notes: ''
  });
  const [showOpportunityViewModal, setShowOpportunityViewModal] = useState(false);
  const [showQuoteViewModal, setShowQuoteViewModal] = useState(false);
  const [selectedOpportunityForView, setSelectedOpportunityForView] = useState(null);
  const [selectedQuoteForView, setSelectedQuoteForView] = useState(null);
  const [formErrors, setFormErrors] = useState({});

  const { user } = useAuth();
  const currentUserId = user?._id || user?.id;
  const isSalesManager = user?.role === USER_ROLES.SALES_MANAGER || user?.role === USER_ROLES.EXPORT_MANAGER;
  const isSalesExecutive = user?.role === USER_ROLES.SALES_EXECUTIVE || user?.role === USER_ROLES.EXPORT_EXECUTIVE || user?.role === USER_ROLES.BUSINESS_RELATIONSHIP_MANAGER || user?.role === USER_ROLES.BUSINESS_DEVELOPMENT_EXECUTIVE;

  // Initial filter values from URL (for persistence across refresh)
  const initialSearch = searchParams.get('search') || '';
  const initialStatus = searchParams.get('status') || 'all';
  const initialOwner = searchParams.get('owner') || 'all';
  const initialStartDate = searchParams.get('startDate') || '';
  const initialEndDate = searchParams.get('endDate') || '';
  const initialPage = Number(searchParams.get('page')) || 1;
  const initialSortBy = searchParams.get('sortBy') || null;
  const initialSortDir = searchParams.get('sortOrder') || 'asc';
  const isInitialPageResetMount = useRef(true);

  const [filterOwner, setFilterOwner] = useState(initialOwner);
  const [assigneeSearchTerm, setAssigneeSearchTerm] = useState("");
  const [assigneeOptions, setAssigneeOptions] = useState([]);
  const [filteredAssigneeOptions, setFilteredAssigneeOptions] = useState([]);
  const [loadingAssignees, setLoadingAssignees] = useState(false);
  const [openAssigneeDropdown, setOpenAssigneeDropdown] = useState(false);
  const [assigneeDropdownPosition, setAssigneeDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const assigneeDropdownRef = useRef(null);

  // New state variables for search and filter
  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const [selectedStatus, setSelectedStatus] = useState(initialStatus);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [pageSize] = useState(10); // Items per page
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(initialSearch);
  const debouncedSearchTermRef = useRef(initialSearch);
  const [dateRange, setDateRange] = useState({ startDate: initialStartDate, endDate: initialEndDate });
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showInlineSelectedFormats, setShowInlineSelectedFormats] = useState(false);
  const [inlineFormatPanelEntered, setInlineFormatPanelEntered] = useState(false);
  const selectedExportWrapRef = useRef(null);
  const [sortColumn, setSortColumn] = useState(initialSortBy);
  const [sortDirection, setSortDirection] = useState(initialSortDir);

  // Sync filter/pagination/sort state to URL for persistence across refresh
  useEffect(() => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);
      const managedKeys = ['search', 'status', 'owner', 'startDate', 'endDate', 'page', 'sortBy', 'sortOrder'];
      managedKeys.forEach(key => params.delete(key));

      if (debouncedSearchTerm.trim()) params.set('search', debouncedSearchTerm.trim());
      if (selectedStatus && selectedStatus !== 'all') params.set('status', selectedStatus);
      if (filterOwner && filterOwner !== 'all') params.set('owner', filterOwner);
      if (dateRange.startDate) params.set('startDate', dateRange.startDate);
      if (dateRange.endDate) params.set('endDate', dateRange.endDate);
      if (currentPage && currentPage !== 1) params.set('page', String(currentPage));
      if (sortColumn) {
        params.set('sortBy', sortColumn);
        if (sortDirection && sortDirection !== 'asc') params.set('sortOrder', sortDirection);
      }

      return params;
    }, { replace: true });
  }, [
    debouncedSearchTerm,
    selectedStatus,
    filterOwner,
    dateRange,
    currentPage,
    sortColumn,
    sortDirection,
    setSearchParams,
  ]);
  // Delete state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [opportunityToDelete, setOpportunityToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const queryClient = useQueryClient();

  // Debounce search term
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      debouncedSearchTermRef.current = searchTerm;
    }, 350);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  // Handle click outside to close assignee dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        openAssigneeDropdown &&
        assigneeDropdownRef.current &&
        !assigneeDropdownRef.current.contains(event.target) &&
        !event.target.closest('[data-dropdown-opportunity-assignee-filter]')
      ) {
        setOpenAssigneeDropdown(false);
        setAssigneeSearchTerm('');
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openAssigneeDropdown]);

  // Handle window scroll/resize to update dropdown position
  useEffect(() => {
    const updatePosition = () => {
      if (openAssigneeDropdown && assigneeDropdownRef.current) {
        const rect = assigneeDropdownRef.current.getBoundingClientRect();
        setAssigneeDropdownPosition({
          top: rect.bottom + window.scrollY,
          left: rect.left + window.scrollX,
          width: rect.width
        });
      }
    };

    if (openAssigneeDropdown) {
      updatePosition();
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
    }

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [openAssigneeDropdown]);

  // Load CRM users for assignee selection (matching TasksFollowups/LeadListFilters pattern)
  const loadAssignees = useCallback(async () => {
    if (isSalesExecutive) {
      setAssigneeOptions([]);
      setFilteredAssigneeOptions([]);
      return;
    }

    setLoadingAssignees(true);
    try {
      const params = new URLSearchParams();
      params.append('limit', 'all');

      if (user?.role === USER_ROLES.SUPER_ADMIN) {
        params.append('roleFilter', 'sales');
      } else if (isSalesManager) {
        params.append('roleFilter', user?.role === USER_ROLES.EXPORT_MANAGER ? 'export_executive' : 'sales_executive');
        params.append('managerId', user._id || user.id);
      }

      const url = `${API_ENDPOINTS.CRM.TEAM_MANAGEMENT.USERS}${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await authFetch(url, {
        credentials: 'include',
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();

      let exportManagers = [];
      if (user?.role === USER_ROLES.SALES_MANAGER || user?.role === USER_ROLES.EXPORT_MANAGER) {
        try {
          const emResponse = await authFetch(`${API_ENDPOINTS.CRM.TEAM_MANAGEMENT.USERS}?limit=all&roleFilter=sales`, {
        credentials: 'include',
            method: 'GET',
            headers: {
              'Content-Type': 'application/json'
            }
          });
          const emResult = await emResponse.json();
          if (emResult.responseCode === 200 && emResult.data?.users) {
            exportManagers = emResult.data.users.filter(u => u.role === USER_ROLES.EXPORT_MANAGER || u.designation === 'Export Manager');
          }
        } catch (e) {
        }
      }

      if (result.responseCode === 200 && result.data?.users) {
        const crmUsers = result.data.users.map(u => ({
          id: u.user || u._id || u.id,
          name: u.name || 'Unknown',
          email: u.email || '',
          role: u.role || u.designation || ''
        }));
        const mappedExportManagers = exportManagers.map(u => ({
          id: u.user || u._id || u.id,
          name: u.name || 'Unknown',
          email: u.email || '',
          role: u.role || u.designation || ''
        }));

        const allUsers = [...crmUsers, ...mappedExportManagers];
        const uniqueUsersMap = new Map();
        allUsers.forEach(u => {
          if (u.id) uniqueUsersMap.set(u.id, u);
        });
        const mergedUsers = Array.from(uniqueUsersMap.values());

        // If the logged-in user is a manager, add them to the list
        const managerOption = (user?.role === USER_ROLES.SALES_MANAGER || user?.role === USER_ROLES.EXPORT_MANAGER)
          ? [{
            id: currentUserId,
            name: `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.name || 'Me',
            email: user?.email || '',
            role: user?.role || ''
          }]
          : [];

        const baseOptions = [
          { id: 'all', name: 'All Representatives', email: '', role: '' },
        ];
        setAssigneeOptions([...baseOptions, ...managerOption, ...mergedUsers]);
        setFilteredAssigneeOptions([...baseOptions, ...managerOption, ...mergedUsers]);
      }
    } catch (error) {
      const defaultOptions = [{ id: 'all', name: 'All Representatives', email: '', role: '' }];
      setAssigneeOptions(defaultOptions);
      setFilteredAssigneeOptions(defaultOptions);
    } finally {
      setLoadingAssignees(false);
    }
  }, [user, isSalesManager, isSalesExecutive, currentUserId]);

  // Load assignees on mount
  useEffect(() => {
    loadAssignees();
  }, [loadAssignees]);

  // Filter assignee options based on search term
  useEffect(() => {
    if (!openAssigneeDropdown) return;
    const handler = setTimeout(() => {
      if (!assigneeSearchTerm.trim()) {
        setFilteredAssigneeOptions(assigneeOptions);
      } else {
        const term = assigneeSearchTerm.toLowerCase();
        const filtered = assigneeOptions.filter(option =>
          option.name.toLowerCase().includes(term) ||
          option.email.toLowerCase().includes(term)
        );
        setFilteredAssigneeOptions(filtered);
      }
    }, 300);
    return () => clearTimeout(handler);
  }, [assigneeSearchTerm, openAssigneeDropdown, assigneeOptions]);

  // Fetch KPIs separately - calculated from Opportunity model only (closed_won/closed_lost)
  const { data: kpiData, isLoading: kpiLoading } = useQuery({
    queryKey: ['closeWonLostKPIs', filterOwner],
    queryFn: async () => {
      const result = await opportunityService.getCloseWonLostKPIs({
        ownerId: filterOwner
      });

      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch KPIs');
      }

      return result.stats || {
        totalOpportunities: 0,
        totalWonCount: 0,
        totalLostCount: 0,
        totalWonValue: 0,
        totalLostValue: 0
      };
    },
    retry: false,
    refetchOnWindowFocus: false
  });

  // Fetch close won/lost opportunities only (closed_won and closed_lost stages)
  const { data: opportunitiesData, isLoading: listLoading, error: fetchError, refetch } = useQuery({
    queryKey: ['closeWonLostOpportunities', debouncedSearchTerm, selectedStatus, dateRange, currentPage, filterOwner],
    queryFn: async () => {
      const result = await opportunityService.getCloseWonLostOpportunities({
        search: debouncedSearchTerm,
        stage: selectedStatus,
        startDate: dateRange.startDate || undefined,
        endDate: dateRange.endDate || undefined,
        ownerId: filterOwner,
        page: currentPage,
        limit: pageSize
      });

      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch opportunities');
      }

      // Transform opportunities to match the expected format
      // Only use fields from Opportunity model documentation
      const transformedOpportunities = (result.opportunities || []).map(opportunity => {
        // Get owner name
        const owner = opportunity.owner
          ? `${opportunity.owner.firstName || ''} ${opportunity.owner.lastName || ''}`.trim()
          : 'Unassigned';

        const lead = opportunity.leadId || {};
        const leadName = lead.firstName && lead.lastName
          ? `${lead.firstName} ${lead.lastName}`
          : lead.name || lead.firstName || 'Lead';

        return {
          id: opportunity._id || opportunity.id,
          name: opportunity.name || 'Untitled',
          opportunityId: opportunity.opportunityId,
          leadDisplayId: lead.leadId || lead.opportunityId || '',
          leadMongoId: lead._id || lead.id || '',
          leadName: leadName,
          company: lead.company || 'N/A',
          description: opportunity.description || '',
          stage: opportunity.stage || 'new',
          amount: opportunity.amount || 0,
          currency: opportunity.currency || 'INR',
          expectedCloseDate: opportunity.expectedCloseDate || null,
          actualCloseDate: opportunity.actualCloseDate || null,
          owner: owner,
          assignedDate: opportunity.assignedDate || null,
          source: opportunity.source || 'other',
          forecastCategory: opportunity.forecastCategory || 'pipeline',
          winReason: opportunity.winReason || '',
          leadId: opportunity.leadId,
          notes: opportunity.notes || '',
          tags: Array.isArray(opportunity.tags) ? opportunity.tags : [],
          createdAt: opportunity.createdAt,
          lastActivity: opportunity.updatedAt || opportunity.createdAt
        };
      });

      return {
        opportunities: transformedOpportunities,
        pagination: result.pagination || {}
      };
    },
    retry: false,
    refetchOnWindowFocus: false
  });

  // Combined loading state
  const isLoading = kpiLoading || listLoading;

  // Notify query errors via global toast
  useEffect(() => {
    if (fetchError) {
      toast.error(fetchError.message || 'Failed to fetch opportunities');
    }
  }, [fetchError]);

  // Derived state from React Query
  const opportunitiesToClose = opportunitiesData?.opportunities || [];
  const pagination = opportunitiesData?.pagination || {
    currentPage: 1,
    totalPages: 1,
    totalOpportunities: 0,
    hasNextPage: false,
    hasPrevPage: false,
    limit: pageSize
  };

  // Ensure pagination values are numbers
  const safePagination = {
    currentPage: typeof pagination.currentPage === 'number' ? pagination.currentPage : 1,
    totalPages: typeof pagination.totalPages === 'number' ? pagination.totalPages : 1,
    totalOpportunities: typeof pagination.totalOpportunities === 'number' ? pagination.totalOpportunities : 0,
    hasNextPage: typeof pagination.hasNextPage === 'boolean' ? pagination.hasNextPage : false,
    hasPrevPage: typeof pagination.hasPrevPage === 'boolean' ? pagination.hasPrevPage : false,
    limit: typeof pagination.limit === 'number' ? pagination.limit : pageSize
  };

  // Process lost reasons for the selected opportunity view
  const opportunityLostReasons = useMemo(() => {
    const leadData = selectedOpportunityForView?.leadId;
    if (Array.isArray(leadData?.stageHistory)) {
      const lostHistory = leadData.stageHistory.filter(h => {
        if (!h) return false;
        const isLostStage = h.stage === 'closed_lost' || 
                            (h.toStage && (h.toStage.value === 'closed_lost' || h.toStage === 'closed_lost'));
        return isLostStage && h.reason && h.reason.trim() !== '';
      });

      const sorted = [...lostHistory].sort((a, b) => {
        const dateA = a.changedAt ? new Date(a.changedAt) : new Date(0);
        const dateB = b.changedAt ? new Date(b.changedAt) : new Date(0);
        return dateB - dateA;
      });

      return sorted.map(h => ({
        reason: h.reason,
        date: h.changedAt
      }));
    }
    return [];
  }, [selectedOpportunityForView]);

  // Get dashboard stats from KPI API response
  const dashboardStats = useMemo(() => {
    const stats = kpiData || {
      totalOpportunities: 0,
      totalWonCount: 0,
      totalLostCount: 0,
      totalWonValue: 0,
      totalLostValue: 0
    };

    return {
      totalOpportunities: stats.totalOpportunities || 0,
      totalWonValue: stats.totalWonValue || 0,
      totalLostValue: stats.totalLostValue || 0,
      totalWonCount: stats.totalWonCount || 0,
      totalLostCount: stats.totalLostCount || 0
    };
  }, [kpiData]);

  // Handle page change
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };





  const getProbabilityColor = (probability) => {
    if (probability >= 80) return 'text-green-600';
    if (probability >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getStageColor = (stage) => {
    switch (stage) {
      case 'Qualified': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusDisplay = (stage) => {
    switch (stage) {
      case 'closed_won':
        return { text: 'Close Won', className: 'bg-green-100 text-green-800' };
      case 'closed_lost':
        return { text: 'Close Lost', className: 'bg-red-100 text-red-800' };
      case 'new':
        return { text: 'New', className: 'bg-yellow-100 text-yellow-800' };
      case 'contacted':
        return { text: 'Contacted', className: 'bg-blue-100 text-blue-800' };
      case 'qualified':
        return { text: 'Qualified', className: 'bg-purple-100 text-purple-800' };
      default:
        return { text: 'Pending', className: 'bg-yellow-100 text-yellow-800' };
    }
  };


  const showNotification = useCallback((message, type = 'info') => {
    if (type === 'error') {
      toast.error(message);
    } else if (type === 'success') {
      toast.success(message);
    } else if (type === 'warning') {
      toast(message, { icon: '⚠️' });
    } else {
      toast(message, { icon: 'ℹ️' });
    }
  }, []);

  const handleViewOpportunity = (opportunityId) => {
    const opportunities = Array.isArray(opportunitiesToClose) ? opportunitiesToClose : [];
    const opportunity = opportunities.find(o => o.id === opportunityId);
    if (opportunity) {
      setSelectedOpportunityForView(opportunity);
      setShowOpportunityViewModal(true);
    } else {
      showNotification('Opportunity not found!', 'error');
    }
  };

  const handleViewQuote = (quoteId) => {
    // Find quote in all opportunities
    let foundQuote = null;
    for (const opportunity of opportunitiesToClose) {
      const quote = opportunity.quotes.find(q => q.id === quoteId);
      if (quote) {
        foundQuote = { ...quote, opportunityName: opportunity.name, opportunityAccount: opportunity.account };
        break;
      }
    }

    if (foundQuote) {
      setSelectedQuoteForView(foundQuote);
      setShowQuoteViewModal(true);
    } else {
      showNotification('Quote not found!', 'error');
    }
  };

  const handleCloseOpportunityViewModal = () => {
    setShowOpportunityViewModal(false);
    setSelectedOpportunityForView(null);
  };

  const handleCloseQuoteViewModal = () => {
    setShowQuoteViewModal(false);
    setSelectedQuoteForView(null);
  };

  const handleCreateOrder = (opportunityId) => {
    const opportunity = opportunitiesToClose.find(o => o.id === opportunityId);
    if (!opportunity) {
      showNotification('Opportunity not found!', 'error');
      return;
    }

    setOrderForm({
      opportunityId: opportunityId,
      orderNumber: `ORD-${Date.now()}`,
      customerName: opportunity.account,
      customerEmail: `${opportunity.account.toLowerCase().replace(/\s+/g, '')}@example.com`,
      billingAddress: `${opportunity.account} Address`,
      shippingAddress: `${opportunity.account} Address`,
      products: opportunity.products.map(product => ({
        ...product,
        orderQuantity: product.quantity
      })),
      subtotal: opportunity.amount,
      tax: opportunity.amount * 0.18, // 18% GST
      total: opportunity.amount * 1.18,
      paymentTerms: 'Net 30',
      deliveryDate: formatDate(new Date(Date.now() + 14 * 24 * 60 * 60 * 1000), { includeTime: false }),
      notes: `Order created from opportunity: ${opportunity.name}`
    });
    setShowOrderModal(true);
  };

  const handleViewOrder = (orderId) => {
    const order = orders.find(o => o.id === orderId);
    setSelectedOrder(order);
    setShowOrderViewModal(true);
  };

  const handleOrderFormChange = (field, value) => {
    setOrderForm(prev => {
      const updated = { ...prev, [field]: value };

      // Recalculate totals when products change
      if (field === 'products') {
        const subtotal = updated.products.reduce((sum, product) => sum + (product.orderQuantity * product.unitPrice), 0);
        const tax = subtotal * 0.18;
        updated.subtotal = subtotal;
        updated.tax = tax;
        updated.total = subtotal + tax;
      }

      return updated;
    });

    if (formErrors[`order_${field}`]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`order_${field}`];
        return newErrors;
      });
    }
  };

  const handleCreateOrderSubmit = () => {
    const requiredFields = ['customerName', 'customerEmail'];
    const validation = validateRequiredFields(orderForm, requiredFields);

    if (!validation.isValid) {
      const orderErrors = {};
      Object.keys(validation.errors).forEach(key => {
        orderErrors[`order_${key}`] = validation.errors[key];
      });
      setFormErrors(orderErrors);
      showNotification('Please fill in all required fields.', 'error');
      return;
    }

    if (orderForm.products.length === 0) {
      showNotification('Please add at least one product.', 'error');
      return;
    }

    setFormErrors({});

    setIsCreatingOrder(true);
    showNotification('Creating order...', 'info');

    setTimeout(() => {
      const newOrder = {
        id: orders.length + 1,
        ...orderForm,
        status: 'Draft',
        createdAt: formatDate(new Date(), { includeTime: false }),
        orderDate: formatDate(new Date(), { includeTime: false })
      };

      setOrders(prev => [...prev, newOrder]);
      setIsCreatingOrder(false);
      showNotification('Order created successfully!', 'success');
      setShowOrderModal(false);
      setOrderForm({
        opportunityId: '',
        orderNumber: '',
        customerName: '',
        customerEmail: '',
        billingAddress: '',
        shippingAddress: '',
        products: [],
        subtotal: 0,
        tax: 0,
        total: 0,
        paymentTerms: 'Net 30',
        deliveryDate: '',
        notes: ''
      });
    }, 2000);
  };

  const handleCloseOrderModal = () => {
    setShowOrderModal(false);
    setOrderForm({
      opportunityId: '',
      orderNumber: '',
      customerName: '',
      customerEmail: '',
      billingAddress: '',
      shippingAddress: '',
      products: [],
      subtotal: 0,
      tax: 0,
      total: 0,
      paymentTerms: 'Net 30',
      deliveryDate: '',
      notes: ''
    });
  };

  const handleCloseOrderViewModal = () => {
    setShowOrderViewModal(false);
    setSelectedOrder(null);
  };

  // Removed handleBulkClose function as bulk selection is no longer used
  // const handleBulkClose = (type) => {
  //   if (selectedOpportunities.length === 0) {
  //     showNotification('Please select opportunities first', 'error');
  //     return;
  //   }
  //   showNotification(`Bulk ${type} for ${selectedOpportunities.length} opportunities - Feature will be implemented soon`, 'info');
  //   // Here you would typically make an API call to close multiple opportunities
  // };

  // Removed toggleOpportunitySelection function as bulk selection is no longer used
  // const toggleOpportunitySelection = (opportunityId) => {
  //   // Removed functionality
  // };

  // Selection handlers
  const handleSelectRow = (id) => {
    setSelectedRows(prev => {
      const newSelection = new Set(prev);
      if (newSelection.has(id)) {
        newSelection.delete(id);
      } else {
        newSelection.add(id);
      }
      return newSelection;
    });
  };

  const handleSelectAll = (items) => {
    if (items.length === 0) {
      setSelectedRows(new Set());
      return;
    }

    const allSelected = items.every(item => selectedRows.has(item.id));

    setSelectedRows(prev => {
      const newSelection = new Set(prev);
      if (allSelected) {
        items.forEach(item => newSelection.delete(item.id));
      } else {
        items.forEach(item => newSelection.add(item.id));
      }
      return newSelection;
    });
  };

  // Single delete handler
  const handleDeleteClick = (opportunity) => {
    setOpportunityToDelete(opportunity);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!opportunityToDelete) return;
    setIsDeleting(true);
    try {
      const response = await opportunityService.deleteOpportunity(opportunityToDelete.id);
      if (response.success) {
        toast.success('Opportunity deleted successfully');
        queryClient.invalidateQueries(['closeWonLostOpportunities']);
        setSelectedRows(prev => {
          const next = new Set(prev);
          next.delete(opportunityToDelete.id);
          return next;
        });
      } else {
        toast.error(response.error || 'Failed to delete opportunity');
      }
    } catch (error) {
      toast.error('Failed to delete opportunity');
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
      setOpportunityToDelete(null);
    }
  };

  // Bulk delete handler
  const handleBulkDelete = () => {
    if (selectedRows.size === 0) return;
    setShowBulkDeleteConfirm(true);
  };

  const confirmBulkDelete = async () => {
    setIsBulkDeleting(true);
    try {
      const ids = Array.from(selectedRows);
      const response = await opportunityService.bulkDeleteOpportunities(ids);
      if (response.success) {
        toast.success(`${ids.length} opportunit${ids.length === 1 ? 'y' : 'ies'} deleted successfully`);
        queryClient.invalidateQueries(['closeWonLostOpportunities']);
        setSelectedRows(new Set());
      } else {
        toast.error(response.error || 'Failed to delete opportunities');
      }
    } catch (error) {
      toast.error('Failed to delete opportunities');
    } finally {
      setIsBulkDeleting(false);
      setShowBulkDeleteConfirm(false);
    }
  };

  // Opportunities are already filtered by backend
  // Ensure it's always an array to prevent map errors
  const filteredOpportunities = Array.isArray(opportunitiesToClose) ? opportunitiesToClose : [];

  const sortedOpportunities = useMemo(() => {
    if (!sortColumn) return filteredOpportunities;
    return [...filteredOpportunities].sort((a, b) => {
      let valA = a[sortColumn];
      let valB = b[sortColumn];
      if (valA === null || valA === undefined) valA = '';
      if (valB === null || valB === undefined) valB = '';
      if (sortColumn === 'amount') {
        valA = Number(valA) || 0;
        valB = Number(valB) || 0;
      } else if (sortColumn === 'expectedCloseDate' || sortColumn === 'lastActivity') {
        valA = valA ? new Date(valA).getTime() : 0;
        valB = valB ? new Date(valB).getTime() : 0;
      } else if (typeof valA === 'string') {
        valA = valA.toLowerCase();
        valB = (valB || '').toLowerCase();
      }
      if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
      if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredOpportunities, sortColumn, sortDirection]);

  // Check if any filters are active
  const hasActiveFilters = searchTerm !== '' || selectedStatus !== 'all' || dateRange.startDate !== '' || dateRange.endDate !== '' || filterOwner !== 'all';

  // Clear all filters
  const handleClearFilters = () => {
    setSearchTerm('');
    setSelectedStatus('all');
    setDateRange({ startDate: '', endDate: '' });
    setFilterOwner('all');
    setCurrentPage(1);
  };

  // Reset to page 1 when filters change (skip on initial mount so the
  // URL-restored page is preserved on refresh)
  useEffect(() => {
    if (isInitialPageResetMount.current) {
      isInitialPageResetMount.current = false;
      return;
    }
    setCurrentPage(1);
  }, [selectedStatus, debouncedSearchTerm, filterOwner]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const baseExportFilters = useMemo(
    () => ({
      search: debouncedSearchTerm.trim() || undefined,
      stage: selectedStatus !== 'all' ? selectedStatus : undefined,
      startDate: dateRange.startDate || undefined,
      endDate: dateRange.endDate || undefined,
      ownerId: filterOwner !== 'all' ? filterOwner : undefined,
    }),
    [
      debouncedSearchTerm,
      selectedStatus,
      dateRange.startDate,
      dateRange.endDate,
      filterOwner,
    ]
  );

  const currentPageIdsForExport = useMemo(
    () => sortedOpportunities.map((o) => String(o.id)).filter(Boolean),
    [sortedOpportunities]
  );

  const requestBackendExport = useCallback(
    async ({
      scope,
      format,
      selectedIds = [],
      overrideFilters = null,
      overridePage = null,
      overrideLimit = null,
      currentPageIds: overrideCurrentPageIds = null,
    }) => {
      const pageIds =
        scope === 'currentPage'
          ? (overrideCurrentPageIds ?? currentPageIdsForExport)
          : [];
      const response = await authFetch(
        `${process.env.REACT_APP_API_BASE_URL}/exports/generate`, {
        credentials: 'include',
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
                      },
          body: JSON.stringify({
            module: 'crm-close-won-lost',
            format,
            scope,
            filters: {
              ...baseExportFilters,
              ...(overrideFilters != null ? overrideFilters : {}),
            },
            page: overridePage ?? currentPage,
            limit: overrideLimit ?? pageSize,
            sortBy: 'updatedAt',
            sortOrder: 'desc',
            listSortColumn: sortColumn || null,
            listSortDirection: sortDirection,
            selectedIds,
            currentPageIds: Array.isArray(pageIds) ? pageIds : [],
          }),
        }
      );

      if (!response.ok) {
        const text = await response.text();
        let msg = text || 'Export request failed';
        try {
          const j = JSON.parse(text);
          if (j?.responseMessage || j?.message) {
            msg = j.responseMessage || j.message;
          }
        } catch {
          /* plain text */
        }
        throw new Error(msg);
      }

      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition') || '';
      const filenameMatch =
        disposition.match(/filename\*=UTF-8''([^;]+)/i) ||
        disposition.match(/filename="?([^"]+)"?/i);
      const fallbackName = `close-won-lost-export.${format === 'xlsx' ? 'xlsx' : format}`;
      const filename = filenameMatch?.[1]
        ? decodeURIComponent(filenameMatch[1]).replace(/(^"|"$)/g, '')
        : fallbackName;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    [
      baseExportFilters,
      currentPage,
      pageSize,
      currentPageIdsForExport,
      sortColumn,
      sortDirection,
    ]
  );

  const handleCloseWonLostSelectedFormatExport = useCallback(
    async (format) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) return;
      const ids = sortedOpportunities
        .filter((o) => selectedRows.has(o.id))
        .map((o) => String(o.id))
        .filter(Boolean);
      if (!ids.length) {
        toast.error('No selected rows to export.');
        return;
      }
      setExportLoading(true);
      try {
        await requestBackendExport({
          scope: 'selected',
          format,
          selectedIds: ids,
        });
        toast.success(`${ids.length} opportunit${ids.length === 1 ? 'y' : 'ies'} exported.`);
        setShowInlineSelectedFormats(false);
        setSelectedRows(new Set());
      } catch (error) {
        toast.error(error?.message || 'Export failed.');
      } finally {
        setExportLoading(false);
      }
    },
    [selectedRows, requestBackendExport, sortedOpportunities]
  );

  const handleCloseWonLostScopedExport = useCallback(
    async (scope, format, meta = {}) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) {
        return;
      }

      const pageItems = sortedOpportunities;
      const totalFiltered = Number(safePagination.totalOpportunities ?? 0);

      setExportLoading(true);
      try {
        if (scope === 'currentPage' && !pageItems.length) {
          toast.error('No data on current page to export.');
          return;
        }

        if (scope === 'filtered' && totalFiltered === 0) {
          toast.error('No opportunities match the current filters.');
          return;
        }

        if (scope === 'dateRange') {
          const rangeStart = meta?.startDate;
          const rangeEnd = meta?.endDate;
          if (!rangeStart || !rangeEnd) {
            toast.error('Please select start and end dates.');
            return;
          }
          await requestBackendExport({
            scope: 'dateRange',
            format,
            overrideFilters: {
              search: baseExportFilters.search,
              stage: baseExportFilters.stage,
              ownerId: baseExportFilters.ownerId,
              startDate: rangeStart,
              endDate: rangeEnd,
            },
          });
        } else if (scope === 'all') {
          await requestBackendExport({
            scope: 'all',
            format,
            overrideFilters: {},
            overridePage: 1,
            overrideLimit: 1000,
          });
        } else if (scope === 'filtered') {
          await requestBackendExport({
            scope: 'filtered',
            format,
          });
        } else if (scope === 'currentPage') {
          await requestBackendExport({
            scope: 'currentPage',
            format,
          });
        } else {
          return;
        }

        toast.success('Export downloaded.');
        setShowExportModal(false);
      } catch (error) {
        toast.error(error?.message || 'Export failed. Please try again.');
      } finally {
        setExportLoading(false);
      }
    },
    [
      baseExportFilters,
      safePagination.totalOpportunities,
      sortedOpportunities,
      requestBackendExport,
      sortColumn,
      sortDirection,
    ]
  );

  const handleExportClick = () => {
    if (selectedRows.size > 0) {
      setShowInlineSelectedFormats((open) => !open);
      return;
    }
    setShowInlineSelectedFormats(false);
    setShowExportModal(true);
  };

  useEffect(() => {
    if (selectedRows.size === 0) {
      setShowInlineSelectedFormats(false);
    }
  }, [selectedRows.size]);

  useEffect(() => {
    if (!showInlineSelectedFormats) {
      setInlineFormatPanelEntered(false);
      return;
    }
    const id = requestAnimationFrame(() =>
      requestAnimationFrame(() => setInlineFormatPanelEntered(true))
    );
    return () => cancelAnimationFrame(id);
  }, [showInlineSelectedFormats]);

  useEffect(() => {
    if (!showInlineSelectedFormats) return;
    const onMouseDown = (e) => {
      if (
        selectedExportWrapRef.current &&
        !selectedExportWrapRef.current.contains(e.target)
      ) {
        setShowInlineSelectedFormats(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [showInlineSelectedFormats]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Close Won/Lost</h1>
          <p className="text-gray-600">Manage opportunity closures with proper validation and audit trails</p>
        </div>
        <div className="flex items-center space-x-3">
          {selectedRows.size > 0 && user?.role === 'super_admin' && (
            <PermissionGate permission="opportunity_management" action="delete">
              <Button
                variant="outline"
                icon={<Trash2 className="h-4 w-4" />}
                onClick={handleBulkDelete}
                className="w-full sm:w-auto h-10 text-red-600 border-red-300 hover:bg-red-50"
              >
                Delete ({selectedRows.size})
              </Button>
            </PermissionGate>
          )}
          <PermissionGate permission="opportunity_management" action="read">
            <div className="relative shrink-0" ref={selectedExportWrapRef}>
              <Button
                variant="outline"
                icon={<Download className="h-4 w-4" />}
                onClick={handleExportClick}
                disabled={exportLoading || isLoading}
                className={`w-full sm:w-auto h-10${
                  selectedRows.size > 0 ? ' bg-blue-50 border-blue-200 text-blue-600' : ''
                }`}
              >
                {selectedRows.size > 0
                  ? `Export Selected (${selectedRows.size})`
                  : 'Export'}
              </Button>
              <SelectedExportFormatBar
                show={selectedRows.size > 0 && showInlineSelectedFormats}
                panelEntered={inlineFormatPanelEntered}
                disabled={exportLoading}
                onSelect={handleCloseWonLostSelectedFormatExport}
              />
            </div>
          </PermissionGate>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="flex flex-wrap gap-3">
        <div className="bg-white border border-gray-200 rounded-lg p-6 flex-1 min-w-[200px]">
          <div className="flex flex-col items-center justify-between gap-3">
            <div className='flex items-center gap-3'>
              <div className="p-2 bg-blue-100 rounded-lg">
                <Target className="h-6 w-6 text-blue-600" />
              </div>
              <p className="text-sm font-medium text-gray-600">Total Opportunities</p>
            </div>
            <p className="text-2xl font-bold text-gray-900">{dashboardStats.totalOpportunities}</p>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6 flex-1 min-w-[200px]">
          <div className="flex flex-col items-center justify-between gap-3">
            <div className='flex items-center gap-3'>
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <p className="text-sm font-medium text-gray-600">Won</p>
            </div>
            <p className="text-2xl font-bold text-green-600">{dashboardStats.totalWonCount}</p>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6 flex-1 min-w-[200px]">
          <div className="flex flex-col items-center justify-between gap-3">
            <div className='flex items-center gap-3'>
              <div className="p-2 bg-red-100 rounded-lg">
                <XCircle className="h-6 w-6 text-red-600" />
              </div>
              <p className="text-sm font-medium text-gray-600">Lost</p>
            </div>
            <p className="text-2xl font-bold text-red-600">{dashboardStats.totalLostCount}</p>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6 flex-1 min-w-[200px]">
          <div className="flex flex-col items-center justify-between gap-3">
            <div className='flex items-center gap-3'>
              <div className="p-2 bg-green-100 rounded-lg">
                <IndianRupee className="h-6 w-6 text-green-600 font-bold text-xl" />
              </div>
              <p className="text-sm font-medium text-gray-600">Won Value</p>
            </div>
            <p className="text-2xl font-bold text-green-600">₹{formatIndianAmount(dashboardStats.totalWonValue)}</p>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6 flex-1 min-w-[200px]">
          <div className="flex flex-col items-center justify-between gap-3">
            <div className='flex items-center gap-3'>
              <div className="p-2 bg-red-100 rounded-lg">
                <IndianRupee className="h-6 w-6 text-red-600 font-bold text-xl" />
              </div>
              <p className="text-sm font-medium text-gray-600">Lost Value</p>
            </div>
            <p className="text-2xl font-bold text-red-600">₹{formatIndianAmount(dashboardStats.totalLostValue)}</p>
          </div>
        </div>
      </div>

      {/* Search and Filter Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
        <div className="flex flex-col lg:flex-row gap-3 lg:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search lead..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1); // Reset to first page when search changes
              }}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent w-full"
            />
          </div>
          <div className="flex flex-col sm:flex-row gap-3 lg:flex-shrink-0">
            {!isSalesExecutive && (
              <div className="relative">
                <button
                  ref={assigneeDropdownRef}
                  onClick={() => {
                    const isOpening = !openAssigneeDropdown;
                    setOpenAssigneeDropdown(isOpening);
                    if (isOpening) {
                      setFilteredAssigneeOptions(assigneeOptions);
                    }
                  }}
                  disabled={loadingAssignees}
                  className="w-full sm:w-[220px] px-3 py-2 border border-gray-300 rounded-lg flex items-center justify-between text-left bg-white focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  <span className="truncate text-sm whitespace-nowrap">
                    {assigneeOptions.find(opt => opt.id === filterOwner)?.name || 'All Representatives'}
                  </span>
                  <ChevronDown className={`w-4 h-4 text-gray-400 flex-shrink-0 ml-2 transition-transform ${openAssigneeDropdown ? 'rotate-180' : ''}`} />
                </button>

                {/* Assignee Filter Dropdown */}
                {openAssigneeDropdown && (
                  <div
                    data-dropdown-opportunity-assignee-filter
                    className="fixed z-[9999] bg-white border border-gray-200 rounded-lg shadow-xl"
                    style={{
                      top: `${assigneeDropdownPosition.top}px`,
                      left: `${assigneeDropdownPosition.left}px`,
                      minWidth: '240px',
                      maxWidth: '350px',
                      width: `${Math.max(240, assigneeDropdownPosition.width)}px`
                    }}
                  >
                    <div className="p-2 border-b border-gray-100">
                      <div className="relative">
                        <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="text"
                          value={assigneeSearchTerm}
                          onChange={(e) => {
                            const value = e.target.value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 100);
                            setAssigneeSearchTerm(value);
                          }}
                          placeholder="Search assignees..."
                          maxLength={100}
                          className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                          autoFocus
                        />
                      </div>
                    </div>
                    <div className="overflow-y-auto max-h-[240px] overflow-x-hidden">
                      {loadingAssignees && filteredAssigneeOptions.length === 0 ? (
                        <div className="p-3 text-sm text-gray-500 text-center">
                          Loading assignees...
                        </div>
                      ) : filteredAssigneeOptions.length === 0 ? (
                        <div className="p-3 text-sm text-gray-500 text-center">
                          {assigneeSearchTerm ? 'No assignees found' : 'No assignees available'}
                        </div>
                      ) : (
                        <div className="flex flex-col">
                          {filteredAssigneeOptions.map(option => (
                            <button
                              key={option.id}
                              type="button"
                              onClick={() => {
                                setFilterOwner(option.id);
                                setCurrentPage(1);
                                setOpenAssigneeDropdown(false);
                                setAssigneeSearchTerm('');
                              }}
                              className={`w-full text-left px-3 py-2 text-sm border-b border-gray-100 last:border-b-0 flex flex-col ${filterOwner === option.id
                                ? 'bg-green-50 text-green-600 font-medium'
                                : 'hover:bg-gray-50 text-gray-700'
                                }`}
                            >
                              <div className="truncate font-medium break-words">{option.name}</div>
                              {(option.email || option.role) && (
                                <div className="text-xs text-gray-500 truncate break-words flex flex-wrap items-center gap-1.5 mt-0.5">
                                  {option.email && <span>{option.email}</span>}
                                  {option.email && option.role && <span className="text-gray-300">•</span>}
                                  {option.role && <span className="capitalize px-1.5 py-0.25 bg-gray-100 rounded text-[10px] text-gray-600 font-medium">{option.role.replace('_', ' ')}</span>}
                                </div>
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
            <div className="w-full max-w-sm min-w-[150px]">
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="won">Won</option>
                <option value="lost">Lost</option>
              </select>
            </div>
            <DateRangeFilter
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              onChange={(newRange) => {
                setDateRange(newRange);
                setCurrentPage(1);
              }}
              allowFutureDates={true}
            />
            {hasActiveFilters && (
              <Button
                variant="outline"
                icon={<X className="h-4 w-4" />}
                onClick={handleClearFilters}
                className="flex-shrink-0 whitespace-nowrap"
              >
                Clear
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Removed Tabs */}
      <div className="bg-white border border-gray-200 rounded-lg">
        <div className="p-6">
          {/* Loading spinner for content */}
          {isLoading && (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-agrosaf-green"></div>
              <span className="ml-3 text-gray-700">Loading opportunities...</span>
            </div>
          )}

          {/* All Opportunities Content */}
          {!isLoading && (
            <div className="space-y-6">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left">
                        <input
                          type="checkbox"
                          className="h-4 w-4 text-agrosaf-green focus:ring-agrosaf-green border-gray-300 rounded cursor-pointer"
                          checked={sortedOpportunities.length > 0 && sortedOpportunities.every(opp => selectedRows.has(opp.id))}
                          onChange={() => handleSelectAll(sortedOpportunities)}
                        />
                      </th>

                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Lead ID
                      </th>

                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('leadName')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Opportunity Details</span>
                          {sortColumn === 'leadName' ? (
                            sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                          ) : (
                            <ArrowUpDown className="h-4 w-4 text-gray-400" />
                          )}
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('stage')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Stage</span>
                          {sortColumn === 'stage' ? (
                            sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                          ) : (
                            <ArrowUpDown className="h-4 w-4 text-gray-400" />
                          )}
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('amount')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Amount</span>
                          {sortColumn === 'amount' ? (
                            sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                          ) : (
                            <ArrowUpDown className="h-4 w-4 text-gray-400" />
                          )}
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('expectedCloseDate')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Expected Close</span>
                          {sortColumn === 'expectedCloseDate' ? (
                            sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                          ) : (
                            <ArrowUpDown className="h-4 w-4 text-gray-400" />
                          )}
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('owner')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Owner</span>
                          {sortColumn === 'owner' ? (
                            sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                          ) : (
                            <ArrowUpDown className="h-4 w-4 text-gray-400" />
                          )}
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('lastActivity')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Last Activity</span>
                          {sortColumn === 'lastActivity' ? (
                            sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                          ) : (
                            <ArrowUpDown className="h-4 w-4 text-gray-400" />
                          )}
                        </div>
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {sortedOpportunities.map((opportunity) => (
                      <tr key={opportunity.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            className="h-4 w-4 text-agrosaf-green focus:ring-agrosaf-green border-gray-300 rounded cursor-pointer"
                            checked={selectedRows.has(opportunity.id)}
                            onChange={() => handleSelectRow(opportunity.id)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap">
                          {opportunity.leadMongoId ? (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/lead_detail_360/${opportunity.leadMongoId}`);
                              }}
                              className="text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline bg-blue-50 hover:bg-blue-100 px-2 py-1 rounded transition-colors"
                              title="View Lead Details"
                            >
                              {opportunity.leadDisplayId || 'View Lead'}
                            </button>
                          ) : (
                            <span className="text-xs text-gray-400">N/A</span>
                          )}
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900 capitalize truncate max-w-[200px]" title={opportunity.leadName}>
                            {opportunity.leadName}
                          </div>
                          <div className="text-xs text-gray-500 capitalize truncate max-w-[200px]" title={opportunity.company}>
                            {opportunity.company}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${getStatusDisplay(opportunity.stage).className}`}>
                            {getStatusDisplay(opportunity.stage).text}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          ₹{formatIndianAmount(opportunity.amount || 0)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {opportunity.expectedCloseDate ? formatDate(opportunity.expectedCloseDate, { includeTime: true }) : 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">
                          {opportunity.owner || 'Unassigned'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {opportunity.lastActivity ? formatDate(opportunity.lastActivity, { includeTime: true }) : 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            <PermissionGate permission="opportunity_management" action="read">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewOpportunity(opportunity.id);
                                }}
                                className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50"
                                title="View"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                            </PermissionGate>
                            {user?.role === 'super_admin' && (
                              <PermissionGate permission="opportunity_management" action="delete">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteClick(opportunity);
                                  }}
                                  className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50"
                                  title="Delete"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </PermissionGate>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {(!filteredOpportunities || filteredOpportunities.length === 0) && !isLoading && (
                <div className="text-center py-12 bg-white rounded-lg">
                  <Target className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No opportunities found</h3>
                  <p className="text-gray-500">Try adjusting your search or filter criteria</p>
                </div>
              )}
              {/* Pagination */}
              {safePagination && safePagination.totalPages > 1 && (
                <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
                  <div className="text-sm text-gray-700">
                    Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, safePagination.totalOpportunities || 0)} of {safePagination.totalOpportunities || 0} opportunities
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={!safePagination.hasPrevPage || isLoading}
                    >
                      Previous
                    </Button>
                    <div className="flex items-center space-x-1">
                      {Array.from({ length: Math.min(5, safePagination.totalPages || 1) }, (_, i) => {
                        let pageNum;
                        if ((safePagination.totalPages || 1) <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= (safePagination.totalPages || 1) - 2) {
                          pageNum = (safePagination.totalPages || 1) - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        return (
                          <button
                            key={pageNum}
                            onClick={() => handlePageChange(pageNum)}
                            disabled={isLoading}
                            className={`px-3 py-1 text-sm rounded ${currentPage === pageNum
                              ? 'bg-blue-600 text-white'
                              : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                              }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={!safePagination.hasNextPage || isLoading}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Create Order Modal */}
      {showOrderModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 !mt-0">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Create Order</h3>
              <button onClick={handleCloseOrderModal} className="text-gray-400 hover:text-gray-600">
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center space-x-2">
                  <Package className="h-5 w-5 text-blue-600" />
                  <h4 className="text-lg font-medium text-blue-900">Order Information</h4>
                </div>
                <p className="text-blue-700 mt-2">Create a new order from the selected opportunity.</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Order Number *</label>
                  <input type="text" value={orderForm.orderNumber} onChange={(e) => { const value = handleTextInputChange(e, null, FIELD_LIMITS.customerName); handleOrderFormChange('orderNumber', value); }} maxLength={FIELD_LIMITS.customerName} className={`w-full px-3 py-2 pr-16 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${formErrors.order_orderNumber ? 'border-red-500' : 'border-gray-300'}`} />
                  {formErrors.order_orderNumber && <p className="mt-1 text-xs text-red-500">{formErrors.order_orderNumber}</p>}
                </div>
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Customer Name *</label>
                  <input type="text" value={orderForm.customerName} onChange={(e) => { const value = handleTextInputChange(e, null, FIELD_LIMITS.customerName); handleOrderFormChange('customerName', value); }} maxLength={FIELD_LIMITS.customerName} className={`w-full px-3 py-2 pr-16 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${formErrors.order_customerName ? 'border-red-500' : 'border-gray-300'}`} />
                  {formErrors.order_customerName && <p className="mt-1 text-xs text-red-500">{formErrors.order_customerName}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Customer Email *</label>
                  <input type="email" value={orderForm.customerEmail} onChange={(e) => handleOrderFormChange('customerEmail', e.target.value)} className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${formErrors.order_customerEmail ? 'border-red-500' : 'border-gray-300'}`} />
                  {formErrors.order_customerEmail && <p className="mt-1 text-xs text-red-500">{formErrors.order_customerEmail}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payment Terms</label>
                  <select value={orderForm.paymentTerms} onChange={(e) => handleOrderFormChange('paymentTerms', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500">
                    <option value="Net 15">Net 15</option>
                    <option value="Net 30">Net 30</option>
                    <option value="Net 45">Net 45</option>
                    <option value="Net 60">Net 60</option>
                    <option value="Due on Receipt">Due on Receipt</option>
                  </select>
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-2">
                <Button variant="outline" onClick={handleCloseOrderModal}>Cancel</Button>
                <PermissionGate permission="order_management" action="create">
                  <Button variant="primary" onClick={handleCreateOrderSubmit} disabled={isCreatingOrder} icon={isCreatingOrder ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div> : <Package className="h-4 w-4" />}>
                    {isCreatingOrder ? 'Creating Order...' : 'Create Order'}
                  </Button>
                </PermissionGate>
              </div>
            </div>
          </div>
        </div>
      )
      }

      {/* View Order Modal */}
      {
        showOrderViewModal && selectedOrder && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 !mt-0">
            <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Order Details - {selectedOrder.orderNumber}</h3>
                <button onClick={handleCloseOrderViewModal} className="text-gray-400 hover:text-gray-600">
                  <X className="h-6 w-6" />
                </button>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <Button variant="outline" onClick={handleCloseOrderViewModal}>Close</Button>
              </div>
            </div>
          </div>
        )
      }

      {/* Opportunity View Modal */}
      {
        showOpportunityViewModal && selectedOpportunityForView && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 transition-opacity !mt-0">
            <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl overflow-hidden transform transition-all border border-gray-100">
              <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between bg-white">
                <div className="flex items-center space-x-3">
                  <div className={`p-2 rounded-lg ${selectedOpportunityForView.stage === 'closed_won' ? 'bg-green-50 text-green-600' : selectedOpportunityForView.stage === 'closed_lost' ? 'bg-red-50 text-red-600' : 'bg-blue-50 text-blue-600'}`}>
                    <Target className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Lead Details</h3>
                    <p className="text-gray-500 text-sm font-medium">{selectedOpportunityForView.leadName || 'Lead'}</p>
                  </div>
                </div>
                <button onClick={handleCloseOpportunityViewModal} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-all">
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="max-h-[70vh] overflow-y-auto p-6 space-y-6 bg-white">
                <div className="bg-gray-50 rounded-2xl p-5 border border-gray-100 space-y-5">
                  <div className="flex items-start space-x-4">
                    <div className="bg-white p-2.5 rounded-xl shadow-sm border border-gray-100"><Target className="h-5 w-5 text-blue-600" /></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Lead Name</p>
                      <p className="text-base font-semibold text-gray-900 leading-tight break-all whitespace-normal capitalize">{selectedOpportunityForView.leadName || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="bg-white p-2.5 rounded-xl shadow-sm border border-gray-100"><User className="h-5 w-5 text-purple-600" /></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Assigned Owner</p>
                      <p className="text-base font-semibold text-gray-900 leading-tight break-all whitespace-normal">{selectedOpportunityForView.owner || 'Unassigned'}</p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-center space-x-4">
                    <div className="bg-green-50 p-3 rounded-lg"><IndianRupee className="h-6 w-6 text-green-600" /></div>
                    <div>
                      <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-0.5">Potential Amount</p>
                      <p className="text-xl font-bold text-gray-900">₹{formatIndianAmount(selectedOpportunityForView.amount || 0)}</p>
                    </div>
                  </div>
                  <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-center space-x-4">
                    <div className="bg-blue-50 p-3 rounded-lg"><Activity className="h-6 w-6 text-blue-600" /></div>
                    <div>
                      <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-0.5">Current Stage</p>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-semibold capitalize ${getStatusDisplay(selectedOpportunityForView.stage).className}`}>
                        {getStatusDisplay(selectedOpportunityForView.stage).text}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 rounded-xl border border-gray-100 bg-gray-50/50">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1 flex items-center"><Calendar className="h-3 w-3 mr-1" /> Expected Close</p>
                    <p className="text-sm font-semibold text-gray-900">{selectedOpportunityForView.expectedCloseDate ? formatDate(selectedOpportunityForView.expectedCloseDate, { includeTime: false }) : 'Not set'}</p>
                  </div>
                  <div className="p-4 rounded-xl border border-gray-100 bg-gray-50/50">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1 flex items-center"><Clock className="h-3 w-3 mr-1" /> Last Activity</p>
                    <p className="text-sm font-semibold text-gray-900">{selectedOpportunityForView.lastActivity ? formatDate(selectedOpportunityForView.lastActivity, { includeTime: true }) : 'N/A'}</p>
                  </div>
                </div>

                <div className="space-y-4">
                  {(selectedOpportunityForView.winReason || opportunityLostReasons.length > 0) && (
                    <div className={`p-4 rounded-xl border ${selectedOpportunityForView.stage === 'closed_won' ? 'bg-green-50 border-green-100' : 'bg-red-50 border-red-100'}`}>
                      <div className="flex items-center mb-2">
                        {selectedOpportunityForView.stage === 'closed_won' ? <CheckCircle className="h-4 w-4 text-green-600 mr-2" /> : <XCircle className="h-4 w-4 text-red-600 mr-2" />}
                        <h4 className={`text-sm font-bold ${selectedOpportunityForView.stage === 'closed_won' ? 'text-green-800' : 'text-red-800'}`}>
                          {selectedOpportunityForView.stage === 'closed_won' ? 'Winning Reason' : 'Loss Reason History'}
                        </h4>
                      </div>
                      {selectedOpportunityForView.stage === 'closed_won' ? (
                        <p className="text-sm text-gray-700 leading-relaxed italic">{selectedOpportunityForView.winReason || 'No reason specified'}</p>
                      ) : (
                        <div className="space-y-3">
                          {opportunityLostReasons.map((lr, index) => {
                            const reasonText = typeof lr === 'object' ? lr.reason : lr;
                            const reasonDate = typeof lr === 'object' ? lr.date : null;
                            return (
                              <div key={index} className={`${index !== 0 ? 'border-t border-red-100 pt-2' : ''}`}>
                                <p className="text-sm text-gray-700 leading-relaxed italic">"{reasonText}"</p>
                                {reasonDate && <p className="text-[10px] text-gray-500 mt-0.5">{formatDate(reasonDate, { includeTime: true })}</p>}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                  {selectedOpportunityForView.description && (
                    <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                      <div className="flex items-center mb-2">
                        <FileText className="h-4 w-4 text-gray-500 mr-2" />
                        <h4 className="text-sm font-bold text-gray-700">Description</h4>
                      </div>
                      <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-wrap break-all first-letter:capitalize">{selectedOpportunityForView.description}</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end">
                <Button variant="outline" onClick={handleCloseOpportunityViewModal} className="hover:bg-white px-8">Close Details</Button>
              </div>
            </div>
          </div>
        )}

      {/* Single Delete Confirmation Modal */}
      {showDeleteConfirm && opportunityToDelete && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-60 flex items-center justify-center z-50 p-4 !mt-0">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6 border border-gray-100">
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-2 bg-red-100 rounded-full">
                <Trash2 className="h-5 w-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Delete Opportunity</h3>
            </div>
            <p className="text-gray-600 mb-1">Are you sure you want to delete this opportunity?</p>
            <p className="text-sm font-medium text-gray-900 mb-5 bg-gray-50 px-3 py-2 rounded-lg truncate">
              {opportunityToDelete.name || opportunityToDelete.opportunityId || 'Untitled'}
            </p>
            <p className="text-xs text-red-600 mb-6 flex items-center">
              <XCircle className="h-3 w-3 mr-1.5 flex-shrink-0" />
              This action cannot be undone.
            </p>
            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={() => { setShowDeleteConfirm(false); setOpportunityToDelete(null); }}
                className="flex-1"
                disabled={isDeleting}
              >
                Cancel
              </Button>
              <button
                onClick={confirmDelete}
                disabled={isDeleting}
                className="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white font-medium py-2 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2"
              >
                {isDeleting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Deleting...</span>
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4" />
                    <span>Delete</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteConfirm && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-60 flex items-center justify-center z-50 p-4 !mt-0">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6 border border-gray-100">
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-2 bg-red-100 rounded-full">
                <Trash2 className="h-5 w-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Delete Opportunities</h3>
            </div>
            <p className="text-gray-600 mb-5">
              Are you sure you want to delete{' '}
              <span className="font-semibold text-red-600">{selectedRows.size}</span>{' '}
              selected opportunit{selectedRows.size === 1 ? 'y' : 'ies'}?
            </p>
            <p className="text-xs text-red-600 mb-6 flex items-center">
              <XCircle className="h-3 w-3 mr-1.5 flex-shrink-0" />
              This action cannot be undone.
            </p>
            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={() => setShowBulkDeleteConfirm(false)}
                className="flex-1"
                disabled={isBulkDeleting}
              >
                Cancel
              </Button>
              <button
                onClick={confirmBulkDelete}
                disabled={isBulkDeleting}
                className="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white font-medium py-2 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2"
              >
                {isBulkDeleting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Deleting...</span>
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4" />
                    <span>Delete All ({selectedRows.size})</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        loading={exportLoading}
        title="Export Close Won/Lost Opportunities"
        showDateRange
        dateRangeAllowFutureEnd
        scopedFormatExport={{
          onExport: handleCloseWonLostScopedExport,
          formats: ['csv', 'xlsx', 'pdf'],
          showCurrentPage: true,
          showFiltered: true,
          showDateRange: true,
          showAll: true,
        }}
      />

    </div>
  );
};

export default CloseWonLost;
